import { TreeNode } from "@shared/ui-native/Tree";
import * as olExtent from 'ol/extent';
import { ModelsLayer } from "./ModelsLayer";
import { IModelInfo } from "@shared/models/types";
import { ModelService } from "@shared/models/model-service";
import { transform as olTransform } from 'ol/proj';
import { LoadingManager } from "@shared/models/loading-manager";
import { Auth } from "@shared/Auth";
import Static from "ol/source/ImageStatic";
import ImageLayer from "ol/layer/Image";
import Feature from "ol/Feature";
import { fromExtent } from 'ol/geom/Polygon';
import Style from "ol/style/Style";
import { Fill, Stroke } from "ol/style";
import { StructureLayer } from "./StructureLayer";
import { TreeNodeUtils } from "@shared/utils/tree-node-utils";


const projection = 'EPSG:3857';

const BASE_URL: string = ModelService.getVizApiBaseUrl();

const color = "rgba(0,0,0,0.0)";

const propStyle = new Style({
    fill: new Fill({
        color: color,
    }),
    stroke: new Stroke({
        color: color,
        width: 5,
    }),
});

export class Model {
    uuid: string;
    node: TreeNode;
    extent: olExtent.Extent;

    meshImageLayer: ImageLayer<Static>;

    hitDetectionFeature: Feature;
    structureLayer: StructureLayer;
    modelsLayer: ModelsLayer;

    modelInfo: IModelInfo;

    originalNodeText: string;


    constructor(node: TreeNode, structureLayer: StructureLayer, modelsLayer: ModelsLayer) {
        this.uuid = node.uuid;
        this.node = node;
        this.structureLayer = structureLayer;
        this.modelsLayer = modelsLayer;
        this.extent = olExtent.createEmpty();
        this.originalNodeText = node.text;
    }

    public async load() {
        const modelStatus = await ModelService.getModelStatus(this.node);
        /*
        If model is structure, we need to wait for mesh image to be available
        and then load it. Surface models are loaded immediately as outlines.
        */

        const fileExtension: string | undefined = this.originalNodeText.split('.').pop()?.toLowerCase();
        if (fileExtension === "ifc") {
            if (modelStatus.has_failed) {
                console.log("Model has been processed invalidly", this.node.text, modelStatus);
                TreeNodeUtils.disableNode(this.node);
                return;
            }

            if (modelStatus.mesh_image_available) {
                console.log("Model mesh image is available", this.node.text);
                await this.loadMeshImage();
                this.node.setLoading(false);
            } else {

                TreeNodeUtils.setNodeToProcessing(this.node);

                const status = await ModelService.pollModelUntilReady(this.node);

                if (status.has_failed) {
                    TreeNodeUtils.disableNode(this.node);
                } else {
                    await this.loadMeshImage();
                    TreeNodeUtils.resetNode(this.node, this.originalNodeText);
                }
            }

        } else {
            await this.loadOutlines();
            this.node.setLoading(false);
        }


    }

    public dispose() {

        if (this.meshImageLayer) {
            this.structureLayer.removeLayer(this.meshImageLayer);
            this.meshImageLayer.setSource(null);
            this.meshImageLayer = null;
        }

        this.structureLayer.removeHitDetectionFeature(this.hitDetectionFeature);
        this.modelsLayer.removeFeaturesBySource({ modelId: this.node.id });
    }


    private async loadMeshImage() {
        const url = `${BASE_URL}/${kuura.getProjectUuid()}/${this.uuid}/mesh-image`

        const token = await Auth.getToken();
        const resp = await fetch(url, {
            headers: { "Authorization": "Bearer " + token.accessToken },
        });
        const data = await resp.json();

        const minCoord = [data.extent[0], data.extent[1]];
        const maxCoord = [data.extent[2], data.extent[3]];
        const min = olTransform(minCoord, "EPSG:4326", "EPSG:3857");
        const max = olTransform(maxCoord, "EPSG:4326", "EPSG:3857");

        const transformedExtent: olExtent.Extent = olExtent.createEmpty();
        olExtent.extendCoordinates(transformedExtent, [min, max]);

        this.extent = transformedExtent;


        // Create a rectangle feature for the mesh image
        this.hitDetectionFeature = new Feature(fromExtent(transformedExtent));
        this.hitDetectionFeature.setStyle(propStyle);
        this.hitDetectionFeature.set('type', 'mesh-image');
        this.hitDetectionFeature.set('id', this.node.id);
        this.hitDetectionFeature.set('fileUuid', this.uuid);
        this.hitDetectionFeature.set('fileName', this.node.text);

        if (this.structureLayer.mouseInteraction) {
            this.hitDetectionFeature.set('mouseInteraction', this.structureLayer.mouseInteraction);
        }


        const source = new Static({
            url: data.dataUrl,
            projection: projection,
            imageExtent: transformedExtent,
            crossOrigin: 'anonymous',
        });

        this.meshImageLayer = new ImageLayer({
            source: source,
            zIndex: 10,
        });

        this.structureLayer.addHitDetectionFeature(this.hitDetectionFeature);
        this.structureLayer.addLayer(this.meshImageLayer);
    }

    private async loadOutlines() {
        LoadingManager.startLoading(this.uuid);

        try {

            const token = await Auth.getToken();

            const vizApiUrl: string = ModelService.getVizApiBaseUrl();
            const url = `${vizApiUrl}/${kuura.getProjectUuid()}/${this.uuid}/outline`;

            const response = await fetch(url, {
                headers: { "Authorization": "Bearer " + token.accessToken },
            });

            const outlineResponse = await response.json();

            const { features } = this.modelsLayer.drawGeoJSON(this.node.id, this.node.text, outlineResponse);
            // get bounds of the features
            const bounds = olExtent.createEmpty();
            for (const feature of features) {
                olExtent.extend(bounds, feature.getGeometry().getExtent());
            }

            this.extent = bounds;
        } catch (e) {
            console.error(e);
            this.node.setError(__('js_map.invalid_coordinates'));
            this.node.setChecked(0, true, false);
        } finally {
            LoadingManager.finishLoading(this.uuid);
        }
    }
}
